import { Fragment, lazy, Suspense, useMemo, useState, type ReactNode } from 'react';
import type { Address, Attachment, MessageDetail, RemoteContentRule, RemoteMode } from '@/api';
import { ApiError } from '@/api/http';
import { MessageFrame } from '@/features/message-frame/MessageFrame';
import type { FrameIsolation } from '@/features/message-frame/IsolatedMessageFrame';
import { useContentOrigin } from '@/features/message-frame/contentOrigin';
import { useApi } from '@/api/provider';
import { openableURL, type FrameAppearance, type FrameFit } from '@/features/message-frame/frameDocument';
import { useOriginalColorsFor } from '@/features/message-frame/originalColors';
import {
  defaultSubresourceFetcher,
  type SubresourceFetcher,
} from '@/features/message-frame/subresources';
import { AttachmentList } from '@/features/attachments/AttachmentList';
import { AddressContactMenu } from '@/features/contacts/AddressContactMenu';
import { RemoteContentBar } from '@/features/remote-content/RemoteContentBar';
import { lazyComponent } from '@/app/lazyWithPreload';

import { useUnsubscribe, type UnsubscribeComposeSeed } from '@/features/unsubscribe/useUnsubscribe';
import { AiSummaryButton, type AiSummaryController } from '@/features/ai-summary';
import { AiSummaryPanel, MessageInviteCard } from '@/app/lazyUi';
import { AnchoredMenu, useAnchoredMenu } from '@/components/AnchoredMenu';
import { Icon } from '@/components/Icon';
import { Sheet } from '@/components/Sheet';
import { useHandheld } from '@/hooks/useMediaQuery';
import { useDarkTheme } from '@/hooks/useTheme';
import { ReadingPaneSkeleton } from '@/components/Skeleton';
import { NotAvailableOffline } from '@/offline/OfflineNotices';
import { cssVars } from '@/utils/css';
import { initialsOf } from '@/app/accounts';
import { displayName, formatFullDate, formatListDate } from '@/utils/format';
import { findLinks } from './links';
import { renderStatusNotice } from './renderStatusNotice';
import { useMarkReadAfter } from './useMarkReadAfter';
import './reading-subresources.css';
import './reading-handheld.css';
import './print.css';

// Only for a message whose SVG images were blocked, after its frame has said
// so: not on the first paint (#577 follow-up).
const SvgNotice = lazyComponent(() => import('@/features/remote-content/SvgNotice'), 'SvgNotice');

/*
 * View source (#173) is the one panel here that is worth its own chunk: it is
 * opened rarely, and it is the only consumer of `api.messages.raw`. Lazy, as
 * WP-109 asks for.
 */
const ViewSourcePanel = lazy(() => import('@/features/view-source/ViewSourcePanel'));

/*
 * Headers and authentication (#326, ADR 0062), lazy for the same reason as
 * view source: opened rarely, and the only consumer of
 * `api.messages.authentication`.
 */
const MessageHeadersPanel = lazy(
  () => import('@/features/message-headers/MessageHeadersPanel'),
);

/*
 * An attached email, opened in the app (ADR 0092). Lazy: opened rarely, and
 * it renders this module's own MessageBodySection, which a static import
 * would make a cycle.
 */
const AttachedMessageViewer = lazy(
  () => import('@/features/attached-message/AttachedMessageViewer'),
);

/** One extra verb for the handheld ⋮ sheet; see `moreActions`. */
export interface ReadingSheetAction {
  label: string;
  onSelect: () => void;
}

export interface ReadingPaneProps {
  message: MessageDetail | undefined;
  isLoading: boolean;
  /**
   * The subject's heading level. 2 in Mail, where the list's title is the
   * page's h1; 1 in the pop-out window, where the message is the page (#205).
   */
  subjectLevel?: 1 | 2;
  /** Failure of `GET /messages/{id}`, if any. */
  error?: Error | undefined;
  /** Refetch the open message. */
  onRetry?: (() => void) | undefined;
  onBack: () => void;
  /**
   * Archive. Absent when the mailbox has no Archive folder to move into: the
   * endpoint answers 404 there, so a button would be an offer that cannot be
   * kept. The caller decides; this pane simply does not draw what it was not
   * given.
   */
  onArchive?: (() => void) | undefined;
  onDelete: () => void;
  /**
   * Report spam — or, when `isJunk`, Not spam (keyboard `!`). Absent when the
   * caller cannot do it here: a mailbox with no Junk folder has nowhere to
   * report to, and the verb is absent rather than disabled.
   */
  onSpam?: (() => void) | undefined;
  /** "Block sender…": opens the caller's confirmation. Absent, no item. */
  onBlockSender?: (() => void) | undefined;
  /**
   * "Add to Tasks" (ADR 0075): opens the caller's form. Absent, no button
   * (the pop-out message window has no Tasks to add to).
   */
  onAddToTasks?: (() => void) | undefined;
  /**
   * "Snooze" (ADR 0076): opens the caller's chooser. Absent, no button (the
   * pop-out window, and mail that cannot be snoozed: a draft).
   */
  onSnooze?: (() => void) | undefined;
  /** Drawn under the subject: the caller's "In Tasks" chip, when this email is a task. */
  taskChip?: ReactNode;
  /**
   * "Label as" (plan/17, keyboard `l`): opens the caller's picker. Absent, no
   * button (the pop-out window).
   */
  onLabel?: (() => void) | undefined;
  /** Drawn under the subject: the message's label chips, each with its ×. */
  labelChips?: ReactNode;
  /**
   * Opens a prefilled composer for a `mailto:` unsubscribe, which the user
   * sends themselves. Absent (the pop-out window has no composer), and a
   * `mailto` unsubscribe is not offered at all.
   */
  onComposeUnsubscribe?: ((seed: UnsubscribeComposeSeed) => void) | undefined;
  onReply: (mode: 'reply' | 'reply-all' | 'forward') => void;
  /**
   * A forward is prepared by the server (it re-stages the original's files),
   * so it takes a moment and it can be refused. While it is in flight the
   * button says so and cannot be pressed twice; a refusal is shown here, on
   * the message it was about.
   */
  forwardBusy?: boolean;
  actionProblem?: string | undefined;
  onToggleFlag: () => void;
  /**
   * The message window (ADR 0020) reuses this pane verbatim — body, attachment
   * list and remote-content bar — but has no list to go back to, and carries
   * two verbs of its own ("Mark unread", "Open in main window"). Rather than a
   * second, drifting copy of the pane, those are the only two differences.
   */
  showBack?: boolean;
  extraActions?: ReactNode;

  /* ------------------------------------------------ handheld (ADR 0048) */

  /**
   * Render the phone form of the pane (plan/15 §7): the action row becomes
   * a sticky app bar of icons with a ⋮ sheet, the replies move to pills after
   * the body, and the recipients fold behind "to … ▾". Defaults to the
   * handheld breakpoint; the message window overrides it, because a desktop
   * popup narrower than 60rem is still a desktop window.
   */
  handheld?: boolean;
  /**
   * Mark unread, as an app-bar icon on handheld. Absent means the caller has
   * no such verb here, and no button is drawn. (On desktop the message window
   * keeps its own text button in `extraActions`.)
   */
  onMarkUnread?: (() => void) | undefined;
  /** "Move to…" in the handheld ⋮ sheet and the desktop ⋮ menu; absent means no such row. */
  onMoveTo?: (() => void) | undefined;
  /**
   * A `mailto:` link in the body was activated: open the in-app composer.
   * Absent, a `mailto:` stays inert, as it always has been.
   */
  onMailto?: ((href: string) => void) | undefined;
  /**
   * Further rows for the handheld ⋮ sheet. `extraActions` is a desktop toolbar
   * fragment and is not rendered on handheld; a caller with verbs of its own
   * lists them here instead.
   */
  moreActions?: readonly ReadingSheetAction[];
  /**
   * Marks the open message read, called under the owner's "Mark as read after
   * N seconds" preference (`useMarkReadAfter`): at once, after the delay if
   * the message is still open, or never. A caller that marks on open itself
   * must not also pass this.
   */
  onMarkRead?: ((message: MessageDetail) => void) | undefined;
  /**
   * Summarize / Explain / Ask with the AI assistant (ADR 0074), from the
   * caller's `useAiSummary(message)`. Nothing is drawn unless it says the
   * assistant is available; absent (as in this pane's own tests), nothing.
   */
  aiSummary?: AiSummaryController | undefined;

  /* ----------------------------------------------- remote content (0018) */

  /** Owning account, for the rule calls the bar makes. */
  accountId?: string;
  /**
   * Show a contact — how "Open contact" leaves this pane when the address in
   * the header is already on a card (`AddressContactMenu`).
   *
   * A callback rather than a `useNavigate()` inside the menu: this pane is
   * rendered outside a router by the message window (ADR 0020) and by its own
   * tests, and a hook that throws there would take the whole pane with it.
   * Absent means the item is absent, not dead.
   */
  onOpenContact?: ((contactId: string) => void) | undefined;
  /**
   * The remote mode the user asked for on *this* message, if any.
   *
   * Owned by the caller because it is part of the detail query's key: opting in
   * re-requests the message with `?remote=load` and re-renders the frame from
   * scratch under a new key, rather than mutating a live frame document.
   */
  remoteMode?: RemoteMode | undefined;
  onRemoteModeChange?: (mode: RemoteMode | undefined) => void;
  /** True in the Junk role folder, where persistence is not offered at all. */
  isJunk?: boolean;
  /** Persisted remote-content rules, so "Stop always loading" can find its row. */
  remoteRules?: readonly RemoteContentRule[];

  /* ------------------------------------------------ subresources (0041) */

  /**
   * How an attachment or an inline part is fetched. Defaults to the shared
   * transport — the single place `X-Session-Proof` is attached — and is
   * overridden only by tests. Must be a stable reference.
   */
  fetchSubresource?: SubresourceFetcher;
}


/** Inline images this message asked for and did not get. */
interface ImageFailure {
  messageId: string;
  /** Asked for, not answered. A retry may help. */
  failed: number;
  /** Refused by this server on policy. A retry cannot help. */
  refused: number;
  /** SVGs the reader has not allowed. The one refusal they can change. */
  svgBlocked: number;
}

/** What the reading pane says when the detail request failed, and what it offers. */
export interface FailureCopy {
  title: string;
  detail: string;
  /** A retry is pointless for an answer (404) and wrong for a dead session (401). */
  retryable: boolean;
}

/**
 * Turns a failed `GET /messages/{id}` into words.
 *
 * The three cases that are *not* "something went wrong" are the point:
 *
 * - **404** — the message moved or was expunged by another client. There is
 *   nothing to retry; the honest action is back to the list.
 * - **409 `folder_invalidated`** — `UIDVALIDITY` changed, so every cached id in
 *   this folder is stale, this one included. `MailView` re-lists from page one
 *   and drops the selection; the pane only has to say so.
 * - **503** — the backend is up and its datastore is not. This must never read
 *   as a sign-out: the session is intact and the transport is already retrying.
 *   A `401`, by contrast, is the session layer's business (it raises the
 *   re-auth dialog over the app) and the pane deliberately offers no retry of
 *   its own that would race it.
 */
export function failureCopy(error: Error): FailureCopy {
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return {
        title: 'This message is no longer in this folder',
        detail: 'It was moved or deleted somewhere else. Go back to the list for the current state.',
        retryable: false,
      };
    }
    if (error.isFolderInvalidated) {
      return {
        title: 'This folder changed on the server',
        detail: 'Every message in it is being reloaded from the top.',
        retryable: false,
      };
    }
    if (error.isSessionExpired) {
      return {
        title: 'Your session needs confirming',
        detail: 'Enter your password in the prompt and this message will load.',
        retryable: false,
      };
    }
    if (error.isServiceUnavailable) {
      return {
        title: 'Temporarily unavailable, retrying',
        detail: 'The mail store is not answering right now. You are still signed in.',
        retryable: true,
      };
    }
    if (error.isOffline) {
      return {
        title: 'You’re offline',
        detail: 'This message will load once the connection is back.',
        retryable: true,
      };
    }
    return { title: 'Couldn’t open this message', detail: error.message, retryable: true };
  }
  return { title: 'Couldn’t open this message', detail: error.message, retryable: true };
}

export function ReadingPane({
  message,
  isLoading,
  error,
  onRetry,
  onBack,
  onArchive,
  onDelete,
  onSpam,
  onBlockSender,
  onAddToTasks,
  onSnooze,
  taskChip,
  onLabel,
  labelChips,
  onComposeUnsubscribe,
  onReply,
  forwardBusy = false,
  actionProblem,
  onToggleFlag,
  showBack = true,
  extraActions,
  accountId,
  onOpenContact,
  remoteMode,
  onRemoteModeChange,
  isJunk = false,
  remoteRules,
  fetchSubresource = defaultSubresourceFetcher,
  handheld: handheldProp,
  onMarkUnread,
  onMoveTo,
  onMailto,
  moreActions,
  onMarkRead,
  aiSummary,
  subjectLevel = 2,
}: ReadingPaneProps) {
  const SubjectHeading = subjectLevel === 1 ? 'h1' : 'h2';
  const handheldQuery = useHandheld();
  const handheld = handheldProp ?? handheldQuery;
  const markRead = useMarkReadAfter(message, onMarkRead);
  const unsubscribe = useUnsubscribe({
    message,
    accountId,
    isJunk,
    onCompose: onComposeUnsubscribe,
  });
  /*
   * Summarize / Explain / Ask with the AI assistant (ADR 0074). Only while the
   * assistant is on with a key: otherwise no button, no sheet row, no panel.
   */
  const summary = aiSummary?.available === true ? aiSummary : undefined;


  /**
   * The id whose HTML body the user asked to see as plain text. Storing the id
   * rather than a boolean is what makes the toggle reset itself when a
   * different message is opened, with no effect and no stale state.
   */
  const [plainTextFor, setPlainTextFor] = useState<string | undefined>(undefined);

  /*
   * Subresource state (ADR 0041) — image failures and the retry counter —
   * lives in `MessageBodySection`, next to the frame that reports it.
   */

  /*
   * Handheld state, keyed by message id for the same reason: the ⋮ sheet,
   * the expanded recipients and the Actual-size choice all belong to the
   * message they were opened on, and opening the next one starts clean.
   */
  const [sheetFor, setSheetFor] = useState<string | undefined>(undefined);
  const [detailsFor, setDetailsFor] = useState<string | undefined>(undefined);
  const [actualSizeFor, setActualSizeFor] = useState<string | undefined>(undefined);
  /** The message whose body is wider than the pane, as the frame measured it. */
  const [wideFor, setWideFor] = useState<string | undefined>(undefined);
  /** The message whose raw source is open (#173). Keyed for the same reason. */
  const [sourceFor, setSourceFor] = useState<string | undefined>(undefined);
  /** The message whose headers panel is open (#324). Keyed, for the same reason. */
  const [headersFor, setHeadersFor] = useState<string | undefined>(undefined);

  /*
   * Attachment bytes — downloads and previews alike — are fetched, minted and
   * revoked by `AttachmentList`, which owns an `ObjectUrlRegistry` with the
   * same lifetime this pane used to give downloads: freed when the message
   * changes and when it unmounts. (Inline images have a third registry, inside
   * `MessageFrame`.)
   */

  // Skeleton rows, never a bare spinner: the pane is shaped like a message
  // before it is one, so opening mail does not flash an empty column.
  if (isLoading) return <ReadingPaneSkeleton />;

  if (error) {
    // A body that never reached this device is not a failure — it was never
    // fetched. Offline, that distinction is the whole difference between "the
    // app is broken" and "this one message is not here", so it gets its own
    // state rather than an error card with a Retry that cannot work.
    if (error instanceof ApiError && error.isOffline) {
      return (
        <NotAvailableOffline
          what="message"
          {...(onRetry ? { onRetry } : {})}
          {...(showBack ? { onBack } : {})}
        />
      );
    }
    const copy = failureCopy(error);
    return (
      <div className="empty-state" role="alert">
        <Icon name="mail-unread" size={32} />
        <p className="empty-state__title">{copy.title}</p>
        <p className="muted">{copy.detail}</p>
        <div className="empty-state__actions">
          <button type="button" className="btn" onClick={onBack}>
            Back to list
          </button>
          {copy.retryable && onRetry && (
            <button type="button" className="btn btn--primary" onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="empty-state">
        <Icon name="mail-unread" size={32} />
        <p className="empty-state__title">No message selected</p>
        <p className="muted">
          Pick a message, or press <span className="kbd">j</span> / <span className="kbd">k</span> to
          move through the list.
        </p>
      </div>
    );
  }

  const flagged = message.flags.includes('flagged');
  const showPlainText = plainTextFor === message.id;
  const html = message.bodyHtml;
  const text = message.bodyText;
  const rendersHtml = html !== undefined && html !== '' && !showPlainText;

  const hasBothBodies = html !== undefined && html !== '' && text !== undefined && text !== '';
  /* Without an account there is nothing to ask for the raw source with. */
  const canViewSource = accountId !== undefined && accountId !== '';
  const sourceOpen = canViewSource && sourceFor === message.id;
  /* The same precondition: the authentication summary is an account-scoped call. */
  const headersOpen = canViewSource && headersFor === message.id;
  const sheetOpen = sheetFor === message.id;
  const detailsOpen = detailsFor === message.id;
  const frameFit: FrameFit = handheld && actualSizeFor === message.id ? 'actual' : 'width';
  const bodyIsWide = wideFor === message.id;
  const recipients = `to ${message.to.map(displayName).join(', ') || 'me'}`;

  /**
   * One address in the header, as its own contact trigger (ADR 0019's menu).
   *
   * Without an account there is nothing to look a contact up in, so the text
   * stays text rather than becoming a button whose menu could only apologise.
   */
  const addressTrigger = (address: Address, label: string, className?: string): ReactNode =>
    accountId === undefined || accountId === '' ? (
      <span className={className}>
        <bdi>{label}</bdi>
      </span>
    ) : (
      <AddressContactMenu
        accountId={accountId}
        address={address}
        handheld={handheld}
        {...(className !== undefined ? { className } : {})}
        {...(onOpenContact !== undefined ? { onOpenContact } : {})}
      >
        <bdi>{label}</bdi>
      </AddressContactMenu>
    );

  /** A comma-separated list in which every name is its own trigger. */
  const addressList = (list: readonly Address[], label: (address: Address) => string): ReactNode =>
    list.map((address, index) => (
      <Fragment key={`${address.email}-${index}`}>
        {index > 0 && ', '}
        {addressTrigger(address, label(address))}
      </Fragment>
    ));
  const detailsId = `reading-details-${message.id}`;

  /** Every sheet row closes the sheet first, so focus has somewhere to return to. */
  const fromSheet = (action: () => void) => () => {
    setSheetFor(undefined);
    action();
  };

  const markUnread =
    onMarkUnread &&
    (() => {
      // The reader's choice outranks a countdown that has not fired yet.
      markRead.cancel();
      onMarkUnread();
    });

  return (
    <>
      {handheld ? (
        /*
          The screen's app bar on a phone (plan/15 §7.1): sticky at the top of
          the document, ← on the left and the verbs as 44-px icons on the
          right. The replies are not here; they are pills after the body,
          where reading ends. Open in new window is absent on purpose (ADR
          0020's popup is a desktop affordance).
        */
        <div className="reading-actions reading-actions--appbar">
          {showBack && (
            <button type="button" className="btn btn--icon pane__back" onClick={onBack} aria-label="Back to list">
              <Icon name="chevron-left" />
            </button>
          )}
          <span className="spacer" />
          {onArchive && (
            <button type="button" className="btn btn--icon" onClick={onArchive} aria-label="Archive">
              <Icon name="archive" />
            </button>
          )}
          <button type="button" className="btn btn--icon" onClick={onDelete} aria-label="Delete">
            <Icon name="trash" />
          </button>
          {markUnread && (
            <button type="button" className="btn btn--icon" onClick={markUnread} aria-label="Mark unread">
              <Icon name="mail-unread" />
            </button>
          )}
          <button
            type="button"
            className="btn btn--icon"
            onClick={onToggleFlag}
            aria-pressed={flagged}
            aria-label={flagged ? 'Remove star' : 'Add star'}
          >
            <Icon name={flagged ? 'star-filled' : 'star'} />
          </button>
          <button
            type="button"
            className="btn btn--icon"
            onClick={() => setSheetFor(message.id)}
            aria-haspopup="dialog"
            aria-expanded={sheetOpen}
            aria-label="More actions"
          >
            <Icon name="more" />
          </button>
        </div>
      ) : (
      <div className="reading-actions">
        {showBack && (
          <button type="button" className="btn btn--icon pane__back" onClick={onBack} aria-label="Back to list">
            <Icon name="chevron-left" />
          </button>
        )}
        {onArchive && (
          <button type="button" className="btn btn--icon" onClick={onArchive} aria-label="Archive (e)" title="Archive (e)">
            <Icon name="archive" />
          </button>
        )}
        {/*
          "Del", not "#". The shortcut moved to the Delete key at the owner's
          request; this label kept advertising the old one, which is worse
          than no hint at all -- it names a key that now does nothing.
        */}
        <button type="button" className="btn btn--icon" onClick={onDelete} aria-label="Delete (Del)" title="Delete (Del)">
          <Icon name="trash" />
        </button>
        {onSpam && (
          <button
            type="button"
            className="btn btn--icon"
            onClick={onSpam}
            aria-label={isJunk ? 'Not spam (!)' : 'Report spam (!)'}
            title={isJunk ? 'Not spam (!)' : 'Report spam (!)'}
          >
            <Icon name={isJunk ? 'inbox' : 'junk'} />
          </button>
        )}
        {/*
          Mark unread is a mail action, so it sits with the other three as an
          icon rather than as a text button appended after Forward -- where
          the pop-out window used to put it, at the owner's request to make it
          an icon. Only for a caller that can mark unread: the mail view marks
          read on open and offers this from the row's context menu instead.
        */}
        {onMarkUnread && (
          <button
            type="button"
            className="btn btn--icon"
            onClick={markUnread}
            aria-label="Mark unread"
            title="Mark unread"
          >
            <Icon name="mail-unread" />
          </button>
        )}
        <button
          type="button"
          className="btn btn--icon"
          onClick={onToggleFlag}
          aria-pressed={flagged}
          aria-label={flagged ? 'Remove star (s)' : 'Add star (s)'}
        >
          <Icon name={flagged ? 'star-filled' : 'star'} />
        </button>
        {onAddToTasks && (
          <button
            type="button"
            className="btn btn--icon"
            onClick={onAddToTasks}
            aria-label="Add to Tasks (Shift+T)"
            title="Add to Tasks (Shift+T)"
          >
            <Icon name="tasks" />
          </button>
        )}
        {onSnooze && (
          <button
            type="button"
            className="btn btn--icon"
            onClick={onSnooze}
            aria-label="Snooze (b)"
            title="Snooze (b)"
          >
            <Icon name="clock" />
          </button>
        )}
        {onLabel && (
          <button
            type="button"
            className="btn btn--icon"
            onClick={onLabel}
            aria-haspopup="dialog"
            aria-label="Label as (l)"
            title="Label as (l)"
          >
            <Icon name="label" />
          </button>
        )}
        <span className="spacer" />
        <button type="button" className="btn btn--ghost" onClick={() => onReply('reply')}>
          <Icon name="reply" /> Reply
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => onReply('reply-all')}>
          <Icon name="reply-all" /> Reply all
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => onReply('forward')}
          disabled={forwardBusy}
        >
          <Icon name="forward" /> {forwardBusy ? 'Forwarding…' : 'Forward'}
        </button>
        {/*
          Print, View headers and View source sit behind ⋮ at the owner's
          request: "we should put Print, View headers, View source under a 3
          dot menu to keep the primary buttons for mail actions."

          They are not mail actions -- they inspect the message rather than
          doing anything to it -- and three text buttons of inspection beside
          Reply/Reply all/Forward made the row read as six equal choices. The
          handheld toolbar already folded them into its own ⋮ sheet; this is
          the same decision on the desktop row.

          `AnchoredMenu` rather than a fourth hand-rolled menu: the sidebar,
          the calendar rail and the contacts rail already share it, and it is
          what portals the menu out of a clipping ancestor (the bug that put
          the folder menu behind the message list).
        */}
        {summary && <AiSummaryButton summary={summary} />}
        {/* The caller's own buttons stay buttons; ⋮ is last on the row. */}
        {extraActions}
        <MessageToolsMenu
          canViewSource={canViewSource}
          {...(onMoveTo ? { onMoveTo } : {})}
          onHeaders={() => setHeadersFor(message.id)}
          onSource={() => setSourceFor(message.id)}
          onBlockSender={onBlockSender}
        />
      </div>
      )}

      <div className={handheld ? 'pane__scroll reading-scroll--handheld' : 'pane__scroll'}>
        {actionProblem !== undefined && (
          <p className="reading-notice reading-notice--failed" role="alert">
            {actionProblem}
          </p>
        )}
        <header className="reading-header">
          <SubjectHeading className="reading-subject" dir="auto">
            {message.subject || '(no subject)'}
          </SubjectHeading>
          {taskChip}
          {labelChips}
          {handheld ? (
            /*
              Gmail's header (plan/15 §7.2): avatar, sender and a short date on
              one line, and the recipients folded behind "to … ▾". On a phone
              the full address list is opt-in; a one-line truncation of it was
              neither readable nor complete.
            */
            <>
              <div className="reading-meta">
                <span className="avatar avatar--lg" style={cssVars({ '--avatar-color': 'var(--color-accent)', '--avatar-ink': 'var(--color-text-on-accent)' })}>
                  {initialsOf(displayName(message.from))}
                </span>
                <div className="reading-meta__text">
                  <div className="reading-meta__line">
                    {addressTrigger(
                      message.from,
                      displayName(message.from),
                      'reading-meta__from truncate',
                    )}
                    {unsubscribe.trigger}
                    <span className="reading-meta__date">{formatListDate(message.date)}</span>
                  </div>
                  <button
                    type="button"
                    className="reading-meta__toggle"
                    aria-expanded={detailsOpen}
                    aria-controls={detailsId}
                    onClick={() => setDetailsFor(detailsOpen ? undefined : message.id)}
                  >
                    <span className="truncate">{recipients}</span>
                    <Icon name="chevron-down" size={16} />
                  </button>
                </div>
              </div>
              {detailsOpen && (
                <dl className="reading-details" id={detailsId}>
                  <dt>From</dt>
                  <dd>{formatAddress(message.from)}</dd>
                  <dt>To</dt>
                  <dd>{message.to.length > 0 ? addressList(message.to, formatAddress) : 'me'}</dd>
                  {message.cc.length > 0 && (
                    <>
                      <dt>Cc</dt>
                      <dd>{addressList(message.cc, formatAddress)}</dd>
                    </>
                  )}
                  <dt>Date</dt>
                  <dd>{formatFullDate(message.date)}</dd>
                </dl>
              )}
            </>
          ) : (
          <div className="reading-meta">
            <span className="avatar avatar--lg" style={cssVars({ '--avatar-color': 'var(--color-accent)', '--avatar-ink': 'var(--color-text-on-accent)' })}>
              {initialsOf(displayName(message.from))}
            </span>
            <div className="reading-meta__text">
              <div className="reading-meta__from">
                {addressTrigger(message.from, displayName(message.from))}
                {unsubscribe.trigger}
              </div>
              <div className="reading-meta__addresses truncate">
                to {message.to.length > 0 ? addressList(message.to, displayName) : 'me'}
                {message.cc.length > 0 && (
                  <>
                    {' · cc '}
                    {addressList(message.cc, displayName)}
                  </>
                )}
              </div>
            </div>
            <div className="reading-meta__date">{formatFullDate(message.date)}</div>
          </div>
          )}

          {/* Unsubscribe's confirm or result, or why it is not offered here. */}
          {unsubscribe.panel}

          {/*
            The header as paper needs it (#173). `.reading-meta` is built for a
            screen — a contact menu on every name, the recipients folded behind
            a button on a phone — and none of that survives printing, so print
            gets a plain definition list instead. It is `display: none` on
            screen and shown only by print.css, and it is `aria-hidden` because
            for a screen reader it would be the same facts a second time.
          */}
          <dl className="reading-print-details" aria-hidden="true">
            <dt>From</dt>
            <dd>{formatAddress(message.from)}</dd>
            <dt>To</dt>
            <dd>{message.to.length > 0 ? message.to.map(formatAddress).join(', ') : 'me'}</dd>
            {message.cc.length > 0 && (
              <>
                <dt>Cc</dt>
                <dd>{message.cc.map(formatAddress).join(', ')}</dd>
              </>
            )}
            <dt>Date</dt>
            <dd>{formatFullDate(message.date)}</dd>
          </dl>
        </header>

        <MessageBodySection
          message={message}
          handheld={handheld}
          showPlainText={showPlainText}
          onTogglePlainText={() => setPlainTextFor(showPlainText ? undefined : message.id)}
          frameFit={frameFit}
          onWideChange={(wide) =>
            setWideFor((current) =>
              wide ? message.id : current === message.id ? undefined : current,
            )
          }
          beforeBody={summary && <AiSummaryPanel summary={summary} />}
          isJunk={isJunk}
          fetchSubresource={fetchSubresource}
          {...(accountId !== undefined ? { accountId } : {})}
          {...(remoteMode !== undefined ? { remoteMode } : {})}
          {...(onRemoteModeChange !== undefined ? { onRemoteModeChange } : {})}
          {...(remoteRules !== undefined ? { remoteRules } : {})}
          {...(onMailto !== undefined ? { onMailto } : {})}
        />

        {/*
          Reply, Reply all and Forward after the body and the attachments, in
          flow (plan/15 §7.2): the actions you take after reading are where
          reading ends, which is why this screen needs no bottom bar.
        */}
        {handheld && (
          <div className="reading-replies">
            <button type="button" className="btn reading-replies__pill" onClick={() => onReply('reply')}>
              <Icon name="reply" /> Reply
            </button>
            <button type="button" className="btn reading-replies__pill" onClick={() => onReply('reply-all')}>
              <Icon name="reply-all" /> Reply all
            </button>
            <button
              type="button"
              className="btn reading-replies__pill"
              onClick={() => onReply('forward')}
              disabled={forwardBusy}
            >
              <Icon name="forward" /> {forwardBusy ? 'Forwarding…' : 'Forward'}
            </button>
          </div>
        )}
      </div>

      {handheld && (
        /*
          The ⋮ sheet (plan/15 §7.1), on the shell's <Sheet> with plain 48-px
          rows. Each row is drawn only when it can do something: no Move to…
          without a caller that moves, no fit choice for a body that fits.
        */
        <Sheet open={sheetOpen} onClose={() => setSheetFor(undefined)} title="Message actions" hideTitle>
          <div className="reading-sheet">
            <button type="button" className="reading-sheet__item" onClick={fromSheet(() => onReply('reply-all'))}>
              Reply all
            </button>
            {summary && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(() => summary.summarize())}>
                Summarize with AI
              </button>
            )}
            {onAddToTasks && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(onAddToTasks)}>
                Add to Tasks
              </button>
            )}
            {onSnooze && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(onSnooze)}>
                Snooze
              </button>
            )}
            {onLabel && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(onLabel)}>
                Label as…
              </button>
            )}
            {onMoveTo && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(onMoveTo)}>
                Move to…
              </button>
            )}
            {onSpam && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(onSpam)}>
                {isJunk ? 'Not spam' : 'Report spam'}
              </button>
            )}
            {onBlockSender && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(onBlockSender)}>
                Block sender…
              </button>
            )}
            {hasBothBodies && (
              <button
                type="button"
                className="reading-sheet__item"
                onClick={fromSheet(() => setPlainTextFor(showPlainText ? undefined : message.id))}
              >
                {showPlainText ? 'View formatted' : 'View as plain text'}
              </button>
            )}
            {rendersHtml && bodyIsWide && (
              <button
                type="button"
                className="reading-sheet__item"
                onClick={fromSheet(() =>
                  setActualSizeFor(frameFit === 'actual' ? undefined : message.id),
                )}
              >
                {frameFit === 'actual' ? 'Fit to width' : 'Actual size'}
              </button>
            )}
            <button type="button" className="reading-sheet__item" onClick={fromSheet(() => window.print())}>
              Print
            </button>
            {canViewSource && (
              <button
                type="button"
                className="reading-sheet__item"
                onClick={fromSheet(() => setHeadersFor(message.id))}
              >
                View headers
              </button>
            )}
            {canViewSource && (
              <button
                type="button"
                className="reading-sheet__item"
                onClick={fromSheet(() => setSourceFor(message.id))}
              >
                View source
              </button>
            )}
            {moreActions?.map((action) => (
              <button
                type="button"
                className="reading-sheet__item"
                key={action.label}
                onClick={fromSheet(action.onSelect)}
              >
                {action.label}
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {headersOpen && accountId !== undefined && (
        <Suspense fallback={null}>
          <MessageHeadersPanel
            accountId={accountId}
            message={message}
            onClose={() => setHeadersFor(undefined)}
          />
        </Suspense>
      )}

      {sourceOpen && accountId !== undefined && (
        <Suspense fallback={null}>
          <ViewSourcePanel
            accountId={accountId}
            message={message}
            onClose={() => setSourceFor(undefined)}
          />
        </Suspense>
      )}
    </>
  );
}

/** "Name <address>", or the bare address when there is no name. */
/**
 * A plain-text body's paragraph, with its web addresses clickable.
 *
 * A text message has no anchors of its own, so without this its links are
 * words to copy by hand. Detection is `./links`, the composer's; what may
 * open is `openableURL`, the same rule as a link in an HTML body — only
 * http(s), so an email address or a `mailto:` stays text, as it is inert in
 * the frame too (ADR 0052). A `javascript:` "URL" is never a link here: it is
 * not one of the shapes `findLinks` recognises, and `openableURL` would refuse
 * it if it were.
 *
 * The anchor is ours, built by React in the app document, so the click
 * handler always runs and does what the frame's does where it can: show the
 * real destination and ask first, then open it with no opener and no
 * Referer. `target`/`rel` are there for the ways a click handler never sees —
 * a middle click or "open in new tab" — and `title` shows the destination on
 * hover.
 */
/**
 * Everything of one message below its header: the invitation, the remote
 * content bar and the image notices, the plain-text switch, the body itself
 * and the attachment list.
 *
 * `ReadingPane` renders exactly one; the conversation stack
 * (`ConversationPane`, ADR 0077) renders one per expanded message, which is
 * why it is its own component rather than a stretch of the pane's JSX. The
 * choices the pane's handheld ⋮ sheet also makes — plain text, the frame's
 * fit — are the caller's; what only the frame reports (image failures and the
 * retry counter) is kept here.
 */
export interface MessageBodySectionProps {
  message: MessageDetail;
  handheld: boolean;
  showPlainText: boolean;
  onTogglePlainText: () => void;
  frameFit: FrameFit;
  /** The frame measured the body as wider than the pane, or no longer so. */
  onWideChange: (wide: boolean) => void;
  /** Drawn after the notices, above the plain-text switch (the AI panel in `ReadingPane`). */
  beforeBody?: ReactNode;
  accountId?: string;
  remoteMode?: RemoteMode | undefined;
  onRemoteModeChange?: (mode: RemoteMode | undefined) => void;
  isJunk: boolean;
  remoteRules?: readonly RemoteContentRule[];
  fetchSubresource: SubresourceFetcher;
  onMailto?: ((href: string) => void) | undefined;
  /**
   * This body is an email attached to another message (ADR 0092), not a
   * message of the mailbox: its renders, downloads, SVG grant and saves go
   * through that message's `attached/{path}` routes, and `message` is the
   * attached email shaped as a detail (its `id` is only a key).
   */
  attached?: AttachedBodySource | undefined;
  /**
   * Opens an attached email. Absent, this section opens its own viewer; the
   * viewer passes its own, so a nested email opens in place with a way back.
   */
  onViewAttachedMessage?: ((attachment: Attachment, from: HTMLElement) => void) | undefined;
}

/** Where an attached email's body comes from (ADR 0092). */
export interface AttachedBodySource {
  /** The mailbox message it is attached to. */
  messageId: string;
  /** Part ids joined by `-`, outermost first. */
  path: string;
  /** That message's subject and Message-ID, for Save to Nextcloud. */
  outerSubject: string;
  outerRfcMessageId?: string | undefined;
}

export function MessageBodySection({
  message,
  handheld,
  showPlainText,
  onTogglePlainText,
  frameFit,
  onWideChange,
  beforeBody,
  accountId,
  remoteMode,
  onRemoteModeChange,
  isJunk,
  remoteRules,
  fetchSubresource,
  onMailto,
  attached,
  onViewAttachedMessage,
}: MessageBodySectionProps) {
  /*
   * Subresource state (ADR 0041). Every piece of it carries the id of the
   * message it is about, so opening another message resets it by construction
   * rather than through an effect that fires a render late — the same trick
   * `plainTextFor` uses, for the same reason.
   */
  const [imageFailure, setImageFailure] = useState<ImageFailure | undefined>(undefined);
  /** Bumped by "Try again" on the image notice; part of the frame key. */
  const [imageAttempt, setImageAttempt] = useState(0);

  /*
   * The user-content origin (ADR 0078). When this page may frame from one,
   * the body is rendered THERE — minted per frame, with the remote mode the
   * reader asked for decided by the server exactly as the read decided it —
   * and attachments download from there too. Null keeps the srcdoc frame and
   * blob: downloads, as before.
   */
  const api = useApi();
  const contentOrigin = useContentOrigin();

  /*
   * Dark mode inside the message (ADR 0081, #55). A dark app shows the body
   * dark: the sender's own dark design when they made one, an adaptation of
   * their light one otherwise. "Show original colors" is per message, and can
   * be remembered per sender (on this device; it is an appearance, not a
   * grant). A light app is unaffected, and so is paper.
   */
  const darkTheme = useDarkTheme();
  const [originalFor, setOriginalFor] = useState<string | undefined>(undefined);
  const [senderOriginal, setSenderOriginal] = useOriginalColorsFor(message.from.email);
  const originalColors = originalFor === message.id || senderOriginal;
  const appearance: FrameAppearance =
    !darkTheme || originalColors ? 'light' : message.bodyDarkReady ? 'dark' : 'dark-auto';

  const isolatedMessageId = message.id;
  const attachedMessageId = attached?.messageId;
  const attachedPath = attached?.path;
  const isolation = useMemo<FrameIsolation | undefined>(() => {
    if (contentOrigin === null || !accountId || isolatedMessageId === undefined) return undefined;
    const options = (signal: AbortSignal) => ({
      signal,
      ...(remoteMode !== undefined ? { remote: remoteMode } : {}),
      ...(appearance !== 'light' ? { appearance: 'dark' as const } : {}),
    });
    // An attached email renders through its own route, on the same
    // user-content origin, under the same capabilities (ADR 0092, 0078).
    if (attachedMessageId !== undefined && attachedPath !== undefined) {
      return {
        contentOrigin,
        mint: (signal) => api.messages.renderAttached(accountId, attachedMessageId, attachedPath, options(signal)),
        stats: (renderId, signal) =>
          api.messages.renderAttachedStats(accountId, attachedMessageId, attachedPath, renderId, signal),
      };
    }
    return {
      contentOrigin,
      mint: (signal) => api.messages.render(accountId, isolatedMessageId, options(signal)),
      stats: (renderId, signal) =>
        api.messages.renderStats(accountId, isolatedMessageId, renderId, signal),
    };
  }, [api, accountId, contentOrigin, isolatedMessageId, attachedMessageId, attachedPath, remoteMode, appearance]);
  const attachmentLink = useMemo(() => {
    if (isolation === undefined || !accountId || isolatedMessageId === undefined) return undefined;
    if (attachedMessageId !== undefined && attachedPath !== undefined) {
      return (attachment: Attachment) =>
        api.messages
          .attachedPartLink(accountId, attachedMessageId, attachedPath, attachment.id, 'attachment')
          .then((link) => link.url);
    }
    return (attachment: Attachment) =>
      api.messages
        .partLink(accountId, isolatedMessageId, attachment.id, 'attachment')
        .then((link) => link.url);
  }, [api, accountId, isolatedMessageId, attachedMessageId, attachedPath, isolation]);

  /*
   * An attached email opened from this section's list (ADR 0092). The viewer
   * is the section's own unless the caller handles it (the viewer itself
   * does, to open a nested email in place).
   */
  const [viewing, setViewing] = useState<{ messageId: string; path: string } | undefined>(undefined);
  // The message says which mailbox it is in even where the caller does not
  // wire `accountId` (it is only needed for the remote-content rules).
  const viewAccountId = accountId ?? message.accountId;
  const viewAttached =
    onViewAttachedMessage ??
    (viewAccountId !== '' && attached === undefined
      ? (attachment: Attachment) => {
          if (attachment.attachedPath !== undefined) {
            setViewing({ messageId: message.id, path: attachment.attachedPath });
          }
        }
      : undefined);

  const html = message.bodyHtml;
  const text = message.bodyText;
  const rendersHtml = html !== undefined && html !== '' && !showPlainText;
  /**
   * The document is replaced, never mutated, when the remote mode changes (doc
   * 02 §4.1): a new `frameKey` means a new body and a new srcdoc, so
   * placeholders and proxied images never coexist in one document. The old
   * document stays on screen until the new one is resolved (`messageId`), so
   * loading images does not blank the message while they fetch.
   */
  const frameKey = `${message.id}:${message.remoteContent?.mode ?? 'blocked'}:${imageAttempt}`;
  const blockedImages = message.sanitizeReport?.remoteImagesBlocked ?? 0;

  /*
   * Inline parts and sealed remote refs are fetched by the page now (ADR
   * 0041), so they can fail in ways an `<img src>` never could: a rejected
   * proof, an expired session, a dropped connection. That is a different fact
   * from "blocked because you have not asked for remote images" and the pane
   * must not blur them — the remote bar above already owns the policy half of
   * the story, and a message can legitimately be in both states at once.
   */
  const forThisMessage = imageFailure?.messageId === message.id ? imageFailure : undefined;
  const failedImages = forThisMessage?.failed ?? 0;
  const refusedImages = forThisMessage?.refused ?? 0;
  const svgBlockedImages = forThisMessage?.svgBlocked ?? 0;

  /*
   * Inline parts referenced by `cid:` are already drawn inside the frame when
   * the HTML body is what we are showing, so listing them again beside the
   * real attachments is duplicate, confusing chrome. They are not dropped
   * either — they go behind the list's "Show N inline images" disclosure
   * (#172), because a reader looking for "the picture in this mail" should be
   * able to save it.
   */
  const listed = message.attachments.filter(
    (attachment: Attachment) =>
      // The text/calendar alternative an invitation is drawn from. It has no
      // filename, so it lists as a bare "1014 B" row next to the card that
      // already renders it. The .ics attachment beside it keeps its own row:
      // that one the reader may actually want to save.
      attachment.id !== message.invite?.partId,
  );
  const drawnInFrame = (attachment: Attachment): boolean =>
    rendersHtml && attachment.inline && attachment.contentId !== undefined;
  const attachments = listed.filter((attachment) => !drawnInFrame(attachment));
  const inlineAttachments = listed.filter(drawnInFrame);

  const hasBothBodies = html !== undefined && html !== '' && text !== undefined && text !== '';

  return (
    <>
      {/*
        The invitation, if the message carries one. Everything in it is text
        from a `text/calendar` part — untrusted, never markup, and never fed
        to the message frame; `MessageInviteCard` is where that is enforced.
        The account comes off the message rather than the `accountId` prop,
        which is optional and only wired for the remote-content rules.
      */}
      {message.invite && (
        <MessageInviteCard
          accountId={message.accountId}
          messageId={message.id}
          invite={message.invite}
        />
      )}

      {/*
        The remote-content bar (ADR 0018). Only over a rendered HTML body:
        plain text has no images to block, and a bar about images beside a
        text body would be describing something that is not on screen.

        When the backend has not shipped `remote_content` — or the caller does
        not wire the rule actions — the pane falls back to the passive notice
        it has always shown. The count is real either way; what is missing is
        anything to *do* about it, and a button that could only lie is worse
        than no button (ADR 0017's reasoning, unchanged for that case).
      */}
      {rendersHtml &&
        (message.remoteContent !== undefined &&
        accountId !== undefined &&
        onRemoteModeChange !== undefined ? (
          <RemoteContentBar
            message={message}
            accountId={accountId}
            mode={remoteMode}
            onModeChange={onRemoteModeChange}
            isJunk={isJunk}
            attached={attached !== undefined}
            {...(remoteRules ? { rules: remoteRules } : {})}
          />
        ) : (
          blockedImages > 0 && (
            <p className="reading-notice">
              {blockedImages} remote {blockedImages === 1 ? 'image' : 'images'} in this message
              were not loaded.
            </p>
          )
        ))}
      {message.truncated && (
        <p className="reading-notice">This message was cut short because it is very large.</p>
      )}
      {renderStatusNotice(message) !== null && (
        <p className="reading-notice">{renderStatusNotice(message)}</p>
      )}

      {/*
        Not the same sentence as the remote-content bar, and deliberately so.
        "Weren't loaded" up there means a decision was taken on the user's
        behalf; this means a request was made and refused. Saying "blocked"
        here would tell someone whose session just expired that their privacy
        settings are working, which is a lie in the least useful direction.
      */}
      {/*
        A refusal is not a failure, and must not borrow the failure copy.
        These images were asked for and this server said no -- a tracking
        pixel, an SVG it could not make safe to show, or a host the fetch
        guard denies. Saying "the request failed, nothing was blocked, sign
        in again" about them denies the block AND offers a remedy that
        cannot work, so there is no Try again here: nothing about asking a
        second time changes the answer.

        An SVG the reader simply has not allowed is NOT in this count. That
        refusal is the reader's to change, so it gets its own notice with
        the choice in it (ADR 0018's SVG amendment) instead of a sentence
        saying the server will never show it.
      */}
      {refusedImages > 0 && (
        <p className="reading-notice">
          {refusedImages} {refusedImages === 1 ? 'image' : 'images'} in this message{' '}
          {refusedImages === 1 ? 'was' : 'were'} not loaded because this server does not allow{' '}
          {refusedImages === 1 ? 'it' : 'them'} — a tracking pixel, an image it couldn’t make safe
          to show, or a blocked host.
        </p>
      )}
      {svgBlockedImages > 0 && accountId !== undefined && (
        <SvgNotice
          accountId={accountId}
          messageId={attached?.messageId ?? message.id}
          attachedPath={attached?.path}
          address={message.from.email}
          count={svgBlockedImages}
          isJunk={isJunk}
          {...(remoteRules ? { rules: remoteRules } : {})}
          // A new frame key re-resolves the images; the server now says yes.
          onAllowed={() => setImageAttempt((attempt) => attempt + 1)}
        />
      )}

      {failedImages > 0 && (
        <p className="reading-notice reading-notice--failed" role="alert">
          {failedImages} {failedImages === 1 ? 'image' : 'images'} in this message couldn’t be
          fetched from this server — the request failed, nothing was blocked. If you were signed
          out, sign in again first.{' '}
          <button
            type="button"
            className="btn btn--ghost reading-notice__retry"
            onClick={() => setImageAttempt((attempt) => attempt + 1)}
          >
            Try again
          </button>
        </p>
      )}

      {beforeBody}

      {/* On handheld the plain-text choice lives in the ⋮ sheet, with the other view choice. */}
      {((hasBothBodies && !handheld) || (darkTheme && rendersHtml)) && (
        <div className="reading-body__switch">
          {hasBothBodies && !handheld && (
            <button
              type="button"
              className="btn btn--ghost"
              aria-pressed={showPlainText}
              onClick={onTogglePlainText}
            >
              {showPlainText ? 'View formatted' : 'View as plain text'}
            </button>
          )}
          {darkTheme && rendersHtml && (
            <OriginalColorsControl
              original={originalColors}
              sender={message.from.email}
              remembered={senderOriginal}
              onOriginal={(on) => {
                setOriginalFor(on ? message.id : undefined);
                if (!on && senderOriginal) setSenderOriginal(false);
              }}
              onRemember={setSenderOriginal}
            />
          )}
        </div>
      )}

      {/*
        The body switch of docs/plan/11 §9.3. `bodyHtml` reaches the DOM only
        through lane C's `MessageFrame` — a sandboxed, script-less iframe with
        its own CSP. `dangerouslySetInnerHTML` does not appear in this
        directory, and an ESLint rule in eslint.config.js keeps it that way.
        Plain text is rendered as text nodes, never parsed; its web addresses
        become React-built anchors (`PlainTextLinks`), never markup.
      */}
      <article className="reading-body">
        {rendersHtml ? (
          <MessageFrame
            html={html}
            css={message.bodyCss}
            appearance={appearance}
            frameKey={frameKey}
            messageId={message.id}
            isolation={isolation}
            bodyText={text}
            {...(onMailto ? { onMailto } : {})}
            fetchSubresource={fetchSubresource}
            fit={frameFit}
            onFit={({ wide }) => onWideChange(wide)}
            onSubresources={({ failed, refused, svgBlocked }) =>
              setImageFailure(
                failed > 0 || refused > 0 || svgBlocked > 0
                  ? { messageId: message.id, failed, refused, svgBlocked }
                  : undefined,
              )
            }
          />
        ) : text !== undefined && text !== '' ? (
          text.split(/\n{2,}/).map((paragraph, index) => (
            <p key={index}>
              <PlainTextLinks text={paragraph} />
            </p>
          ))
        ) : (
          <p className="muted">This message has no readable body.</p>
        )}
      </article>

      {/*
        The list, not a chip strip (#172): each part says what it is and how
        big it is, and offers a preview when this app can draw the bytes
        itself. `downloadUrl` is used verbatim, never rebuilt from ids — the
        backend mints it (doc 11 §2.4) and only that exact string addresses
        the part. What may be previewed, and why, is argued in
        `features/attachments/preview.ts`.
      */}
      <AttachmentList
        messageId={message.id}
        attachments={attachments}
        inlineAttachments={inlineAttachments}
        fetchSubresource={fetchSubresource}
        {...(attachmentLink ? { downloadLink: attachmentLink } : {})}
        {...(accountId
          ? {
              nextcloudSave:
                attached !== undefined
                  ? {
                      accountId,
                      subject: attached.outerSubject,
                      rfcMessageId: attached.outerRfcMessageId,
                      messageId: attached.messageId,
                      attachedPath: attached.path,
                    }
                  : {
                      accountId,
                      subject: message.subject,
                      rfcMessageId: messageIdHeader(message),
                    },
            }
          : {})}
        handheld={handheld ?? false}
        onViewAttachedMessage={viewAttached}
      />

      {viewing !== undefined && viewing.messageId === message.id && (
        <Suspense fallback={null}>
          <AttachedMessageViewer
            accountId={viewAccountId}
            messageId={viewing.messageId}
            path={viewing.path}
            outerSubject={message.subject}
            outerRfcMessageId={messageIdHeader(message)}
            isJunk={isJunk}
            handheld={handheld}
            fetchSubresource={fetchSubresource}
            {...(onMailto ? { onMailto } : {})}
            onClose={() => setViewing(undefined)}
          />
        </Suspense>
      )}
    </>
  );
}

/**
 * The Message-ID header without its angle brackets, or undefined: what *Save
 * to Nextcloud* re-finds a moved message by (ADR 0075's way, ADR 0086).
 */
function messageIdHeader(message: MessageDetail): string | undefined {
  const header = message.headers.find((h) => h.name.toLowerCase() === 'message-id');
  const value = header?.value.trim().replace(/^<|>$/g, '');
  return value === undefined || value === '' ? undefined : value;
}

/**
 * "Show original colors" (ADR 0081), Gmail's wording, over a body a dark app
 * is showing dark. Once the original colours are on, the reader can make that
 * the rule for this sender, and turn it back off from either place.
 */
function OriginalColorsControl({
  original,
  sender,
  remembered,
  onOriginal,
  onRemember,
}: {
  original: boolean;
  sender: string;
  remembered: boolean;
  onOriginal: (on: boolean) => void;
  onRemember: (on: boolean) => void;
}) {
  return (
    <span className="reading-body__colors">
      <button
        type="button"
        className="btn btn--ghost"
        aria-pressed={original}
        onClick={() => onOriginal(!original)}
      >
        {original ? 'Show in dark mode' : 'Show original colors'}
      </button>
      {original && sender !== '' && (
        <label className="reading-body__colors-remember">
          <input type="checkbox" checked={remembered} onChange={(event) => onRemember(event.target.checked)} />{' '}
          Always for {sender}
        </label>
      )}
    </span>
  );
}

/**
 * The ⋮ menu on the desktop reading toolbar: what you can do *to* a message
 * stays on the row; what merely shows you the message goes in here.
 *
 * Print is always offered — it needs nothing but the document already on
 * screen. The two source views are account-scoped calls, so they appear only
 * where there is an account to make them against, exactly as the buttons they
 * replaced did.
 */
export function MessageToolsMenu({
  canViewSource,
  onMoveTo,
  onHeaders,
  onSource,
  onBlockSender,
  label = 'More actions',
}: {
  canViewSource: boolean;
  onMoveTo?: () => void;
  onHeaders: () => void;
  onSource: () => void;
  /**
   * Block sender is the one verb in here: it does something to the mailbox
   * rather than showing the message, but it is too rare and too weighty for a
   * toolbar icon, and it opens a confirmation anyway.
   */
  onBlockSender?: (() => void) | undefined;
  /** The button's and the menu's name; the conversation stack names whose menu it is. */
  label?: string;
}) {
  const { open, setOpen, anchorRef, menuRef, run } = useAnchoredMenu();

  return (
    <span className="reading-actions__menu-anchor" ref={anchorRef}>
      <button
        type="button"
        className="btn btn--icon"
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
      >
        <Icon name="more" />
      </button>
      {open && (
        <AnchoredMenu
          anchorRef={anchorRef}
          menuRef={menuRef}
          onDismiss={() => setOpen(() => false)}
          className="reading-actions__menu card"
          label={label}
        >
          {/*
            Print (#173) is `window.print()` on this document, under print.css
            — there is no separate print route, because a print route would
            have to inline the sender's HTML into the app document to be worth
            anything. In the pop-out window (ADR 0020) this prints that
            window, which is the one the reader is looking at.
          */}
          {onMoveTo && (
            <button
              type="button"
              role="menuitem"
              className="reading-actions__menu-item"
              onClick={() => run(onMoveTo)}
            >
              Move to…
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="reading-actions__menu-item"
            onClick={() => run(() => window.print())}
          >
            Print
          </button>
          {canViewSource && (
            <button
              type="button"
              role="menuitem"
              className="reading-actions__menu-item"
              onClick={() => run(onHeaders)}
            >
              View headers
            </button>
          )}
          {canViewSource && (
            <button
              type="button"
              role="menuitem"
              className="reading-actions__menu-item"
              onClick={() => run(onSource)}
            >
              View source
            </button>
          )}
          {onBlockSender && (
            <button
              type="button"
              role="menuitem"
              className="reading-actions__menu-item"
              onClick={() => run(onBlockSender)}
            >
              Block sender…
            </button>
          )}
        </AnchoredMenu>
      )}
    </span>
  );
}

function PlainTextLinks({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let at = 0;
  for (const link of findLinks(text)) {
    const url = openableURL(link.href);
    if (url === null) continue;
    parts.push(text.slice(at, link.start));
    parts.push(
      <a
        key={link.start}
        href={url.href}
        target="_blank"
        rel="noopener noreferrer"
        title={url.href}
        onClick={(event) => {
          event.preventDefault();
          if (!window.confirm(`Open ${url.href}?`)) return;
          window.open(url.href, '_blank', 'noopener,noreferrer');
        }}
      >
        {text.slice(link.start, link.end)}
      </a>,
    );
    at = link.end;
  }
  parts.push(text.slice(at));
  return <>{parts}</>;
}

export function formatAddress(address: { name?: string; email: string }): string {
  return address.name ? `${address.name} <${address.email}>` : address.email;
}
